import { Chatbox } from "@talkjs/react";
import { Type } from "@themes/pollinator/ui/Type";
import { Loader } from "@themes/pollinator/ui/Loader";
import { ChatErrorState } from "@themes/pollinator/ui/ChatErrorState";
import { TalkJSData, ChatboxStuckDetector } from "@core/features/chat";
import { User } from "@core/features/auth";

export type Props = {
  talkjsData: TalkJSData | null | undefined;
  user: User | null | undefined;
  /**
   * Conversation to render — resolved by the container as the
   * `talkjs_conversation_id` whose `circle_id` matches the meeting's
   * `circle`. Null when there's no match (the meeting's circle isn't
   * in the user's chat-eligible set, or chat is gated for this user).
   * The empty-state branch handles that case with the existing
   * "you may need to be assigned to a circle" copy.
   */
  conversationId: string | null;
  /**
   * Forwarded to `<ChatboxStuckDetector>`'s `resetSignal` so a Try
   * Again click on the error fallback resets the detector and re-arms
   * the timer. See docs/chat-stuck-spinner-fix-plan.md §5.7.
   */
  sessionRetryKey: number;
  /**
   * Called when the user clicks Try Again. Receives the conversation
   * id captured at click-time so the telemetry payload reflects what
   * the user was actually looking at.
   */
  onSessionRetry: (ctx: { conversationId: string | null }) => void;
  /**
   * Optional telemetry callback fired once when the stuck-detector
   * trips. Receives the conversation id captured at fire-time.
   */
  onChatboxStuckTracked?: (ctx: { conversationId: string | null }) => void;
};

export function VideoMeetingChat({
  talkjsData,
  user,
  conversationId,
  sessionRetryKey,
  onSessionRetry,
  onChatboxStuckTracked,
}: Props) {
  if (!talkjsData || !user) {
    return (
      <div className="flex flex-col gap-8 px-4 py-8">
        <div className="flex flex-row gap-2 items-center">
          <Type variant="h3">Chat</Type>
        </div>
        <div className="flex flex-col gap-2">
          <Loader isFullPage />
          <Type variant="body2" className="text-brand-600 text-center">
            Loading chat data...
          </Type>
        </div>
      </div>
    );
  }

  const sessionReady = Boolean(
    talkjsData.app_id && talkjsData.user_id && talkjsData.token
  );

  if (!sessionReady || !conversationId) {
    return (
      <div className="flex flex-col gap-8 px-4 py-8">
        <div className="flex flex-row gap-2 items-center">
          <Type variant="h3">Chat</Type>
        </div>
        <div className="flex flex-col gap-2">
          <Type variant="h5" className="text-brand-800">
            No conversations available.
          </Type>
          <Type variant="body2" className="text-brand-700">
            You may need to be assigned to a circle to see conversations.
          </Type>
        </div>
      </div>
    );
  }

  // Keying on conversationId forces a remount when the rendered
  // conversation changes (e.g., navigating between two meetings whose
  // page tree was reused), which is what surfaces the
  // `loadingComponent` while TalkJS syncs the new conversation —
  // without the remount, @talkjs/react updates the existing instance
  // in place and the previous conversation lingers on screen.
  //
  // IMPORTANT: <ChatboxStuckDetector> wraps ONLY the live Chatbox
  // branch — the no-conversation and loading branches above already
  // have their own copy. Hoisting this wrapper would fire the stuck
  // timer when there's no Chatbox to be stuck. See
  // docs/chat-stuck-spinner-fix-plan.md §5.7.
  return (
    <ChatboxStuckDetector
      resetSignal={sessionRetryKey}
      onStuck={() => onChatboxStuckTracked?.({ conversationId })}
      fallback={
        <ChatErrorState
          onRetry={() => onSessionRetry({ conversationId })}
        />
      }
    >
      <Chatbox
        key={conversationId}
        conversationId={conversationId}
        theme="pollinator_theme"
        style={{ width: "100%", height: "100%" }}
        loadingComponent={<Loader />}
      />
    </ChatboxStuckDetector>
  );
}

export default VideoMeetingChat;
